/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { use, type ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import { UITarget } from '@/targets/ui-target';
import type { ContentAssetBody } from '@/lib/api/content-asset';
import { useOrderSummaryContext } from './context';

const TERMS_OF_USE_HREF = '/terms-and-conditions';
const PRIVACY_POLICY_HREF = '/privacy-policy';
export const ORDER_SUMMARY_TAX_HELP_COPY_CONTENT_ASSET_ID = 'order-summary-tax-help-copy';

interface OrderSummaryTaxHelpCopyProps {
    className?: string;
    contentAsset?: ContentAssetBody | Promise<ContentAssetBody | null> | null;
}

export function OrderSummaryTaxHelpCopy({
    className,
    contentAsset,
}: OrderSummaryTaxHelpCopyProps): ReactElement | null {
    const { t } = useTranslation('cart');
    const { basket } = useOrderSummaryContext();
    const taxHelpCopyContentAsset =
        contentAsset && typeof contentAsset === 'object' && 'then' in contentAsset ? use(contentAsset) : contentAsset;

    if (basket.taxation === 'gross') return null;

    return (
        <>
            <UITarget targetId="sfcc.orderSummary.taxHelpCopy.before" />
            <UITarget targetId="sfcc.orderSummary.taxHelpCopy">
                <div
                    className={cn('space-y-5 text-sm leading-5 text-primary', className)}
                    data-testid="order-summary-tax-help-copy">
                    {taxHelpCopyContentAsset?.body ? (
                        <div
                            className="[&_a]:underline [&_a]:underline-offset-2 [&_a]:text-primary"
                            data-testid="order-summary-tax-help-copy-content-asset"
                            // Content assets are authored in BM by trusted merchandisers.
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: taxHelpCopyContentAsset.body }}
                        />
                    ) : (
                        <>
                            <p>{t('summary.taxHelpCopyEstimate')}</p>
                            <p>
                                <Trans
                                    i18nKey="cart:summary.taxHelpCopyLegal"
                                    components={{
                                        termsLink: (
                                            <Link
                                                to={TERMS_OF_USE_HREF}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="underline underline-offset-2"
                                                data-testid="order-summary-tax-help-terms-link"
                                            />
                                        ),
                                        privacyLink: (
                                            <Link
                                                to={PRIVACY_POLICY_HREF}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                                className="underline underline-offset-2"
                                                data-testid="order-summary-tax-help-privacy-link"
                                            />
                                        ),
                                    }}
                                />
                            </p>
                        </>
                    )}
                </div>
            </UITarget>
            <UITarget targetId="sfcc.orderSummary.taxHelpCopy.after" />
        </>
    );
}
